import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Wrench, ImageIcon, Send, ImagePlus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import SupplierPicker from "@/components/SupplierPicker";
import BulkImageFinder from "@/components/BulkImageFinder";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, timeAgo, equipmentTotal, equipmentPerUnit } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";
import CostItemsEditor from "@/components/CostItemsEditor";

const EMPTY = { name: "", description: "", supplier_id: null, category_id: null, cost_items: [], units: 1, ff_type: "equipment" };
const NO_CAT = "__none__";
const FF_TYPES = [{ value: "furniture", label: "Furniture" }, { value: "fixtures", label: "Fixtures" }, { value: "equipment", label: "Equipment" }];
const ffLabel = (t) => ({ furniture: "Furniture", fixtures: "Fixtures", equipment: "Equipment" }[t || "equipment"]);

function EqImage({ image, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u, active = true;
    if (image?.storage_path) {
      fetch(`${API}/files/${image.storage_path}`, { credentials: "include" }).then((r) => r.blob()).then((b) => { if (active) { u = URL.createObjectURL(b); setUrl(u); } });
    }
    return () => { active = false; if (u) URL.revokeObjectURL(u); };
  }, [image?.storage_path]);
  if (!image) return <div className={`${className} flex items-center justify-center border border-zinc-200 bg-zinc-50 text-zinc-300`}><Wrench className="h-4 w-4" /></div>;
  return url ? <img src={url} alt="equipment" className={className} /> : <div className={`${className} flex items-center justify-center bg-zinc-100`}><Loader2 className="h-3 w-3 animate-spin text-zinc-400" /></div>;
}

function EquipmentDrawer({ eid, onClose, suppliers, reloadSuppliers, onChange, canManage, categories }) {
  const [e, setE] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const imgRef = useRef();

  const load = () => api.get(`/equipment/${eid}`).then((r) => { setE(r.data); setF({ name: r.data.name, description: r.data.description || "", supplier_id: r.data.supplier_id, category_id: r.data.category_id || null, cost_items: r.data.cost_items || [], units: r.data.units || 1, ff_type: r.data.ff_type || "equipment" }); });
  useEffect(() => { load(); }, [eid]);

  const patch = async (body) => {
    try { const { data } = await api.patch(`/equipment/${eid}`, body); setE(data); onChange?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveFields = async () => { await patch({ name: f.name, description: f.description, cost_items: f.cost_items, units: parseInt(f.units) || 1 }); toast.success("Saved"); };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/equipment/${eid}/notes`, { message: note }); setE(data); setNote(""); onChange?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const uploadImage = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); setBusy(true);
    try { const { data } = await api.post(`/equipment/${eid}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setE(data); onChange?.(); toast.success("Image updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); if (imgRef.current) imgRef.current.value = ""; }
  };
  const remove = async () => {
    if (!window.confirm("Delete this FF&E item?")) return;
    try { await api.delete(`/equipment/${eid}`); toast.success("FF&E deleted"); onChange?.(); onClose(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const notes = [...(e?.notes || [])].reverse();

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto rounded-none border-l border-zinc-200 p-6 sm:max-w-xl">
        <SheetTitle className="sr-only">FF&amp;E details</SheetTitle>
        <SheetDescription className="sr-only">Edit equipment details, supplier and notes</SheetDescription>
        {!e ? (
          <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#e84e34]" /></div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <EqImage image={e.image} className="h-16 w-16 flex-shrink-0 border border-zinc-200 object-cover" />
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-[#e84e34]">FF&amp;E</p>
                <h2 className="mt-1 font-heading text-xl font-bold tracking-tight text-zinc-900" data-testid="drawer-equipment-title">{e.name}</h2>
                <button onClick={() => imgRef.current?.click()} data-testid="equipment-image-upload" className="mt-2 flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-600 hover:border-[#e84e34]"><ImageIcon className="h-3 w-3" /> {e.image ? "Change image" : "Add image"}</button>
                <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={uploadImage} />
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Name</label>
                <input data-testid="equipment-edit-name" value={f.name} onChange={(ev) => setF({ ...f, name: ev.target.value })} className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Description</label>
                <textarea data-testid="equipment-edit-desc" value={f.description} onChange={(ev) => setF({ ...f, description: ev.target.value })} rows={2} className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Type</label>
                <Select value={f.ff_type || "equipment"} onValueChange={(v) => { setF({ ...f, ff_type: v }); patch({ ff_type: v }); }}>
                  <SelectTrigger data-testid="equipment-edit-type" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{FF_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <CostItemsEditor items={f.cost_items} units={f.units} onChange={(ci) => setF({ ...f, cost_items: ci })} onUnitsChange={(u) => setF({ ...f, units: u })} />
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Category</label>
                <Select value={f.category_id || NO_CAT} onValueChange={(v) => { const val = v === NO_CAT ? null : v; setF({ ...f, category_id: val }); patch({ category_id: val }); }}>
                  <SelectTrigger data-testid="equipment-edit-category" className="rounded-none"><SelectValue placeholder="No category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CAT}>No category</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button onClick={saveFields} data-testid="equipment-save" className="bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-zinc-800">Save details</button>
            </div>

            <SupplierPicker value={e.supplier_id} onChange={(id) => patch({ supplier_id: id })} suppliers={suppliers} reload={reloadSuppliers} />
            {e.supplier && <p className="text-xs text-zinc-500">Supplied by <span className="font-medium text-zinc-700">{e.supplier.company_name}</span></p>}

            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wider text-zinc-500">Notes</p>
              <div className="mb-2 space-y-2" data-testid="equipment-notes">
                {notes.length === 0 && <p className="text-xs text-zinc-400">No notes yet</p>}
                {notes.map((n) => (
                  <div key={n.id} className="border border-zinc-200 bg-zinc-50 p-2.5">
                    <p className="text-xs font-medium text-zinc-900">{n.user_name} <span className="font-normal text-zinc-400">· {timeAgo(n.created_at)}</span></p>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-zinc-700">{n.message}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-end gap-2">
                <textarea data-testid="equipment-note-input" value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="Add a note…" rows={2} className="flex-1 resize-none border border-zinc-200 px-2 py-1.5 text-sm outline-none focus:border-[#e84e34]" />
                <button onClick={addNote} disabled={busy} data-testid="equipment-note-submit" className="flex h-9 w-9 items-center justify-center bg-[#e84e34] text-white hover:bg-[#c73e26] disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
              </div>
            </div>

            {canManage && <button onClick={remove} data-testid="equipment-delete" className="flex items-center gap-1 border border-zinc-200 px-3 py-1.5 text-xs text-zinc-500 hover:border-[#e84e34] hover:text-[#e84e34]"><Trash2 className="h-3.5 w-3.5" /> Delete FF&amp;E</button>}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default function Equipment() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const [equipment, setEquipment] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkImgOpen, setBulkImgOpen] = useState(false);
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const loadSuppliers = useCallback(async () => { const s = await api.get("/suppliers"); setSuppliers(s.data); }, []);
  const load = useCallback(async () => {
    const [e, c] = await Promise.all([api.get("/equipment"), api.get("/equipment-categories")]);
    setEquipment(e.data); setCategories(c.data); setLoading(false);
  }, []);
  useEffect(() => { load(); loadSuppliers(); }, [load, loadSuppliers]);

  const catName = (id) => categories.find((c) => c.id === id)?.name;

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/equipment", { name: form.name, description: form.description, supplier_id: form.supplier_id, category_id: form.category_id, ff_type: form.ff_type, cost_items: form.cost_items, units: parseInt(form.units) || 1 });
      if (imageFile) { const fd = new FormData(); fd.append("file", imageFile); await api.post(`/equipment/${data.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      toast.success("FF&E created");
      setCreateOpen(false); setForm(EMPTY); setImageFile(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this FF&E item? This cannot be undone.")) return;
    try { await api.delete(`/equipment/${id}`); toast.success("FF&E deleted"); setEquipment((prev) => prev.filter((x) => x.id !== id)); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-zinc-200 px-6 py-3">
        <p className="text-sm text-zinc-500">{equipment.length} FF&amp;E items</p>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <button onClick={() => setBulkImgOpen(true)} data-testid="bulk-find-images-button" className="flex items-center gap-1.5 border border-[#e84e34] bg-[#fdece8] px-3 py-1.5 text-xs font-medium text-[#e84e34] transition-colors hover:bg-[#e84e34] hover:text-white">
              <ImagePlus className="h-3.5 w-3.5" /> Find images ({selected.length})
            </button>
          )}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <button data-testid="create-equipment-button" className="flex items-center gap-2 bg-[#e84e34] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#c73e26]"><Plus className="h-4 w-4" /> New FF&amp;E</button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading tracking-tight">Add FF&amp;E</DialogTitle>
              <DialogDescription>Track furniture, fixtures &amp; equipment and assign it to store locations.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <input data-testid="equipment-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="FF&E item name" className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              <textarea data-testid="equipment-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={2} className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:border-[#e84e34]" />
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Type</label>
                <Select value={form.ff_type || "equipment"} onValueChange={(v) => setForm({ ...form, ff_type: v })}>
                  <SelectTrigger data-testid="equipment-type-input" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{FF_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <CostItemsEditor items={form.cost_items} units={form.units} onChange={(ci) => setForm({ ...form, cost_items: ci })} onUnitsChange={(u) => setForm({ ...form, units: u })} />
              <SupplierPicker value={form.supplier_id} onChange={(id) => setForm({ ...form, supplier_id: id })} suppliers={suppliers} reload={loadSuppliers} />
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Category</label>
                <Select value={form.category_id || NO_CAT} onValueChange={(v) => setForm({ ...form, category_id: v === NO_CAT ? null : v })}>
                  <SelectTrigger data-testid="equipment-category-input" className="rounded-none"><SelectValue placeholder="No category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_CAT}>No category</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-wider text-zinc-500">Image</label>
                <input type="file" accept="image/*" data-testid="equipment-image-input" onChange={(e) => setImageFile(e.target.files?.[0] || null)} className="block w-full text-xs text-zinc-600 file:mr-3 file:border file:border-zinc-200 file:bg-zinc-50 file:px-3 file:py-1.5 file:text-xs" />
              </div>
            </div>
            <DialogFooter>
              <button data-testid="equipment-submit-button" onClick={create} disabled={saving} className="flex items-center gap-2 bg-[#e84e34] px-4 py-2 text-sm font-medium text-white hover:bg-[#c73e26] disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create FF&amp;E</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#e84e34]" /></div>
      ) : equipment.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-sm text-zinc-400">No FF&amp;E yet. Add your first item.</div>
      ) : (
        <div className="flex-1 overflow-auto p-6">
          <table className="w-full border-collapse border border-zinc-200 bg-white" data-testid="equipment-table">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-medium uppercase tracking-wider text-zinc-500">
                <th className="w-10 px-4 py-3"><input type="checkbox" data-testid="select-all-equipment" checked={equipment.length > 0 && selected.length === equipment.length} onChange={(e) => setSelected(e.target.checked ? equipment.map((x) => x.id) : [])} /></th>
                <th className="px-4 py-3">FF&amp;E item</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Supplier</th>
                <th className="px-4 py-3">Cost</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {equipment.map((e) => (
                <tr key={e.id} data-testid={`equipment-row-${e.id}`} onClick={() => setOpenId(e.id)} className="cursor-pointer border-b border-zinc-100 transition-colors hover:bg-zinc-50">
                  <td className="px-4 py-3" onClick={(ev) => ev.stopPropagation()}><input type="checkbox" data-testid={`select-equipment-${e.id}`} checked={selected.includes(e.id)} onChange={() => toggleSel(e.id)} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <EqImage image={e.image} className="h-10 w-10 flex-shrink-0 border border-zinc-200 object-cover" />
                      <div>
                        <div className="text-sm font-medium text-zinc-900">{e.name}</div>
                        <div className="truncate text-xs text-zinc-400" style={{ maxWidth: 280 }}>{e.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-zinc-500" data-testid={`equipment-type-${e.id}`}>{ffLabel(e.ff_type)}</span></td>
                  <td className="px-4 py-3 text-sm text-zinc-600">{catName(e.category_id) ? <span className="border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-zinc-500">{catName(e.category_id)}</span> : "—"}</td>
                  <td className="px-4 py-3 text-sm text-zinc-600">{e.supplier?.company_name || "—"}</td>
                  <td className="px-4 py-3 text-sm text-zinc-700">
                    <div className="font-medium">{money(equipmentTotal(e))}</div>
                    {(Number(e.units) || 1) > 1 && <div className="text-[11px] text-zinc-400">{money(equipmentPerUnit(e))}/unit · {e.units} units</div>}
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(ev) => ev.stopPropagation()}>
                    {canManage && <button data-testid={`delete-equipment-row-${e.id}`} onClick={() => del(e.id)} title="Delete FF&E item" className="inline-flex items-center gap-1 border border-zinc-200 px-2 py-1 text-xs text-zinc-500 transition-colors hover:border-[#e84e34] hover:text-[#e84e34]"><Trash2 className="h-3.5 w-3.5" /> Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openId && <EquipmentDrawer eid={openId} onClose={() => setOpenId(null)} suppliers={suppliers} reloadSuppliers={loadSuppliers} onChange={load} canManage={canManage} categories={categories} />}
      <BulkImageFinder open={bulkImgOpen} onOpenChange={setBulkImgOpen} entity="equipment" items={equipment.filter((e) => selected.includes(e.id)).map((e) => ({ id: e.id, name: e.name }))} onDone={load} />
    </div>
  );
}
